import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/lembra/PageHeader";
import { PageShell } from "@/components/lembra/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Package, ArrowRight, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { ErrorCard } from "@/components/lembra/ErrorCard";
import { safeFormat } from "@/lib/dates";
// MS526: surface ordersQuery failures to Sentry. The original outage
// (missing GRANT on vendor_partners → 42501 on every SELECT FROM orders)
// hid behind ErrorCard's friendly copy for weeks because nothing
// captured the underlying error. Future loader regressions should
// page within minutes, not get rediscovered via user complaints.
import { Sentry } from "@/lib/sentry";
// Path A: "Needs order" fulfillment runs on /orders (checkout sheet
// or pick-gift drawer) with an explicit Back to Orders affordance.
import {
  useProfile,
  useOrdersList,
  useApprovedCelebrationsPendingOrder,
  isOrderActionNeeded,
  type OrderListRow,
  type ApprovedCelebrationRow,
} from "@/hooks/queries";
import { isPastCelebrationDate } from "@/lib/celebrationDates";
import {
  isGiftRequestOrder,
  parseGiftRequest,
  categoryLabel,
  categoryEmoji,
  occasionLabel,
} from "@/lib/giftRequest";
// MS214 / MS218: category-aware status label helper. Originally inlined
// in this file; lifted into a shared module in MS218 so every surface
// (customer + vendor portal) shares one source of truth.
import { preparingLabel } from "@/lib/vendorCategoryLabels";
import { PageMeta } from "@/components/lembra/PageMeta";
import { ProductCheckoutModals } from "@/components/product-detail/ProductCheckoutModals";
import { useProductCheckoutFlow } from "@/hooks/useProductCheckoutFlow";
import {
  applyCelebrationCheckoutResult,
  openApprovedCelebrationCheckout,
  type CelebrationPickGiftSeed,
} from "@/lib/openApprovedCelebrationCheckout";
import {
  useFloatingConciergeUi,
  useRegisterFloatingConciergeSuppression,
} from "@/contexts/FloatingConciergeContext";
import { ordersFulfillmentReturn } from "@/lib/fulfillmentReturn";
import { BulkOrdersSection } from "@/components/bulk/BulkOrdersSection";

const statusBadge = (
  order: Pick<OrderListRow, "status" | "payment_status" | "vendor_category">,
) => {
  // Action-needed is derived (status=pending + unpaid). The DB column
  // stays "pending", it's the UI that splits "waiting on user" from
  // "waiting on vendor".
  if (isOrderActionNeeded(order)) {
    return <Badge variant="coral">Action needed</Badge>;
  }
  switch (order.status) {
    case "pending":
      return <Badge variant="sky">Pending</Badge>;
    case "confirmed":
      return <Badge variant="navy">Confirmed</Badge>;
    case "delivered":
      return <Badge variant="navy">Delivered</Badge>;
    case "cancelled":
      return <Badge className="bg-charcoal/40 text-white">Cancelled</Badge>;
    case "baking":
      return <Badge variant="navy">{preparingLabel(order.vendor_category)}</Badge>;
    // MS341: was "out_for_delivery" — actual DB enum is "ready" (vendor
    // portal writes this when they Mark as Ready). Customer-facing
    // label stays "Out for delivery" (the narrative the customer needs).
    case "ready":
      return <Badge variant="sky">Out for delivery</Badge>;
    default:
      return <Badge variant="outline">{order.status}</Badge>;
  }
};

// Which statuses roll up under "In Progress" in the filter tab. The tab
// name is a category; the per-card badge stays specific (Confirmed,
// Baking, Out for delivery) so the user still sees where each one sits.
// MS341: switched out_for_delivery → ready to match the actual DB enum;
// pre-fix, "Mark as Ready" orders fell OUT of the In Progress tab on
// the customer's /orders page.
const IN_PROGRESS_STATUSES = new Set(["confirmed", "baking", "ready"]);

// Overdue is a UI-only flag, any order whose delivery date has passed while
// still in a non-terminal state (pending/confirmed/baking/ready)
// needs attention. Underlying status stays unchanged so the operations team
// can move it to delivered/cancelled when they've investigated.
const IN_FLIGHT_STATUSES = new Set(["pending", "confirmed", "baking", "ready"]);
function isOverdue(order: Pick<OrderListRow, "status" | "delivery_date">): boolean {
  if (!order.delivery_date) return false;
  if (!IN_FLIGHT_STATUSES.has(order.status)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // MS439: delivery_date is a date-only Postgres column ("2025-12-31").
  // Parsing without "T00:00:00" gives UTC midnight → wrong comparison
  // near midnight AEST (UTC+10). Force local-time interpretation.
  return new Date(order.delivery_date + "T00:00:00") < today;
}

interface OrderCardProps {
  order: OrderListRow;
  onClick: () => void;
}

function OrderCard({ order, onClick }: OrderCardProps) {
  // category emoji + product + vendor, so reconciled customers see the
  // hamper / workshop / spa they actually bought, not "{name}'s Birthday"
  // with the SKU buried in the detail page.
  if (isGiftRequestOrder(order)) {
    return <GiftRequestCard order={order} onClick={onClick} />;
  }

  const overdue = isOverdue(order);
  const actionNeeded = isOrderActionNeeded(order);
  const cancelled = order.status === "cancelled";
  const delivered = order.status === "delivered";
  const cardPrimaryLabel = orderCardPrimaryLabel(order);

  // Sprint 2.5a B.2-fix-2 (P1-2), unified visual treatment between
  // legacy and chat-originated orders. Was a coloured left-border
  // stripe carrying status info; now a 48px cake-emoji tile matches
  // the GiftRequestCard pattern. Status info stays in the badge where
  // it already lives. The page used to read with two distinct card
  // shapes alternating in one list, visual rhythm broken. One
  // pattern across both shapes; visitors no longer scan two layouts.

  return (
    <button
      onClick={onClick}
      className={`group w-full text-left bg-white rounded-xl border border-charcoal/10 p-5 shadow-sm hover:shadow-md hover:border-navy/20 transition-all ${cancelled ? "opacity-70" : ""}`}
    >
      <div className="flex items-start gap-4">
        {/* Cake emoji tile (legacy cake order). Same 48px square + butter
            background as GiftRequestCard's category tile so both card
            shapes render identical left-rail visual primitive. */}
        <div
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-xl"
          style={{ background: BUTTER_TILE_BG }}
          aria-hidden
        >
          {celebrationTypeEmoji(order.celebration_type)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="font-bold text-navy truncate">
              {order.team_member_name}'s {celebrationLabel(order.celebration_type)}
            </span>
            {statusBadge(order)}
            {overdue && <Badge variant="coral">Overdue</Badge>}
          </div>
          <OrderReferenceLine reference={order.reference} />
          {order.delivery_date && (
            <p className="text-charcoal/50 text-xs mt-1">
              Delivery: {format(new Date(order.delivery_date + "T00:00:00"), "d MMM yyyy")}
            </p>
          )}
        </div>
        {!actionNeeded && (
          <span className="text-navy font-bold text-lg whitespace-nowrap shrink-0">
            ${(order.budget_cents / 100).toFixed(0)}
          </span>
        )}
        {!actionNeeded && !cancelled && (
          <ArrowRight className="h-4 w-4 text-charcoal/30 shrink-0 mt-1" />
        )}
        {cancelled && (
          <RefreshCw className="h-4 w-4 text-charcoal/40 shrink-0 mt-1" />
        )}
      </div>

      {/* Action-needed row: prominent CTA so "Continue" isn't lost in the
          corner next to the arrow. Full-width under a divider so it reads
          as the obvious next action. Works identically on mobile and desktop. */}
      {actionNeeded && (
        <div className="mt-4 pt-4 border-t border-charcoal/10 flex items-center justify-between gap-3">
          <span className="text-charcoal/60 text-sm whitespace-nowrap">
            ${(order.budget_cents / 100).toFixed(0)}{" "}
            <span className="text-charcoal/40">pending payment</span>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-coral text-white px-5 py-2 text-sm font-semibold">
            {cardPrimaryLabel ?? "Finish payment"}
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      )}
      {delivered && (
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-charcoal/10 pt-4">
          <span className="text-sm text-charcoal/55">View delivery record</span>
          <ArrowRight className="h-4 w-4 text-charcoal/30" />
        </div>
      )}
      {!actionNeeded && !delivered && cardPrimaryLabel && (
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-charcoal/10 pt-4">
          <span className="text-sm font-medium text-navy/80">{cardPrimaryLabel}</span>
          <ArrowRight className="h-4 w-4 text-charcoal/35" />
        </div>
      )}
    </button>
  );
}

/**
 * Sprint 1.7r: card for chat / browse-originated orders. Visually
 * differentiated from the legacy cake card, left rail uses a category
 * emoji tile instead of a coloured stripe, and the prose line carries
 * vendor + product instead of "{name}'s {occasion}". The intent is that
 * a freshly-reconciled customer who paid for a Yarra Valley Truffles
 * hamper for "Anya" sees exactly that on their orders page, not a
 * cake-shaped placeholder.
 */
function GiftRequestCard({ order, onClick }: OrderCardProps) {
  const overdue = isOverdue(order);
  const actionNeeded = isOrderActionNeeded(order);
  const cancelled = order.status === "cancelled";
  const delivered = order.status === "delivered";
  const cardPrimaryLabel = orderCardPrimaryLabel(order);
  const parsed = parseGiftRequest(order);
  const occasion = occasionLabel(order.celebration_type);

  return (
    <button
      onClick={onClick}
      className={`group w-full text-left bg-white rounded-xl border border-charcoal/10 p-5 shadow-sm hover:shadow-md hover:border-navy/20 transition-all ${cancelled ? "opacity-70" : ""}`}
    >
      <div className="flex items-start gap-4">
        {/* Sprint 2.5a B.2-fix-1 (P0-2), render real product
            photography when image_url is present (resolved by
            useOrdersList via celebration_recommendations.line_items[0]
            .sku_id → vendor_products.image_url). Falls back to the
            category-emoji tile for legacy orders or chat orders where
            the SKU has no image yet. Same fallback grammar as the
            wishlist + dashboard saved cluster, visual consistency
            across customer surfaces. */}
        {order.image_url ? (
          <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-xl bg-charcoal/5">
            <img
              src={order.image_url}
              alt={`${parsed.product}${parsed.vendor ? ` from ${parsed.vendor}` : ""}`}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-xl"
            style={{ background: BUTTER_TILE_BG }}
            aria-hidden
          >
            {categoryEmoji(parsed.category)}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-2">
            <span className="truncate font-bold text-navy">{parsed.product}</span>
            <Badge
              variant="outline"
              className="border-navy/15 text-[10.5px] uppercase tracking-wider text-navy/70"
            >
              {categoryLabel(parsed.category)}
            </Badge>
            {statusBadge(order)}
            {overdue && <Badge variant="coral">Overdue</Badge>}
          </div>
          <OrderReferenceLine reference={order.reference} />
          <p className="truncate text-sm text-charcoal/85">
            {parsed.vendor ? (
              <span className="text-charcoal/55">{parsed.vendor}</span>
            ) : (
              <span className="text-charcoal/55">Gift order</span>
            )}
          </p>
          <p className="mt-0.5 truncate text-xs text-charcoal/55">
            For {order.team_member_name}
            {order.celebration_type ? ` · ${occasion}` : ""}
            {order.delivery_date
              ? ` · ${format(new Date(order.delivery_date + "T00:00:00"), "d MMM")}`
              : ""}
          </p>
        </div>

        {!actionNeeded && (
          <span className="shrink-0 whitespace-nowrap text-lg font-bold text-navy">
            ${(order.budget_cents / 100).toFixed(0)}
          </span>
        )}
        {!actionNeeded && !cancelled && (
          <ArrowRight className="h-4 w-4 shrink-0 text-charcoal/30 mt-1 group-hover:text-navy transition-colors" />
        )}
        {cancelled && (
          <RefreshCw className="h-4 w-4 shrink-0 text-charcoal/40 mt-1" />
        )}
      </div>

      {actionNeeded && (
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-charcoal/10 pt-4">
          <span className="whitespace-nowrap text-sm text-charcoal/60">
            ${(order.budget_cents / 100).toFixed(0)}{" "}
            <span className="text-charcoal/40">pending payment</span>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-coral px-5 py-2 text-sm font-semibold text-white">
            {cardPrimaryLabel ?? "Finish payment"}
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      )}
      {delivered && (
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-charcoal/10 pt-4">
          <span className="text-sm text-charcoal/55">View delivery record</span>
          <ArrowRight className="h-4 w-4 text-charcoal/30" />
        </div>
      )}
      {!actionNeeded && !delivered && cardPrimaryLabel && (
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-charcoal/10 pt-4">
          <span className="text-sm font-medium text-navy/80">{cardPrimaryLabel}</span>
          <ArrowRight className="h-4 w-4 text-charcoal/35" />
        </div>
      )}
    </button>
  );
}

const memberNameOf = (m: ApprovedCelebrationRow["team_members"]): string => {
  if (!m) return "Team member";
  return Array.isArray(m) ? (m[0]?.name ?? "Team member") : m.name;
};

// Sprint 2.5a /orders-new-redirect, full member resolver. The
// joined team_members shape can be either an object or a single-
// element array depending on supabase-js's FK relationship
// inference; this normalizes both to the shape the chat URL helper
// needs (id + name + start_date).
const memberLiteOf = (
  m: ApprovedCelebrationRow["team_members"],
): { id: string; name: string; start_date: string | null } | null => {
  if (!m) return null;
  const row = Array.isArray(m) ? m[0] : m;
  if (!row) return null;
  return { id: row.id, name: row.name, start_date: row.start_date };
};

const BUTTER_TILE_BG = "rgba(247, 232, 164, 0.6)";

function orderCardPrimaryLabel(
  order: Pick<OrderListRow, "status" | "payment_status" | "cake_size">,
): string | null {
  // One verb for the unpaid / needs-action state across both card
  // shapes (legacy cake + gift request). Was split "Finish payment"
  // (gift) vs "Continue order" (legacy), which read as two different
  // asks for the same "you still owe payment" state.
  if (isOrderActionNeeded(order)) {
    return "Finish payment";
  }
  if (order.status === "delivered") return "View delivery";
  if (order.status === "cancelled") return "View order";
  return null;
}

function celebrationTypeEmoji(type: string): string {
  switch (type) {
    case "birthday":
      return "🎂";
    case "work_anniversary":
      return "🎉";
    case "welcome":
      return "👋";
    case "farewell":
      return "👋";
    case "milestone":
      return "⭐";
    default:
      return "🎁";
  }
}

function OrderReferenceLine({ reference }: { reference: string }) {
  return (
    <p
      className="text-[11px] text-charcoal/45 mt-0.5"
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
    >
      {reference}
    </p>
  );
}

const celebrationLabel = (type: string): string => {
  switch (type) {
    case "birthday":         return "Birthday";
    case "work_anniversary": return "Work Anniversary";
    // MS418 follow-up: "welcome" → "First Day" for label consistency.
    case "welcome":          return "First Day";
    case "farewell":         return "Farewell";
    // MS409: milestone was missing — returned raw "milestone".
    // Default now Title-Cases any unknown type rather than leaking
    // snake_case DB values to customers.
    case "milestone":        return "Milestone";
    default: return type
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
};

// MS390 Bug #16: collapsed from 7 tabs to 4. Pre-MS390:
//   all / action_needed / gifts / pending / in_progress / delivered / cancelled
// Post-MS390:
//   all / active / delivered / cancelled
// Why: action_needed + pending + in_progress + gifts were overlapping
// (action_needed ⊆ pending ⊆ active; gifts was an origin filter rarely
// exercised post-Sprint 1.7+ when chat became the default). The
// orphan-celebrations rail and the action-needed orders are still
// surfaced — they pin to the TOP of the Active tab so the user's
// "things waiting on me" list isn't buried.
//
// Legacy deep-links (e.g. dashboard "See all gift requests") map to
// `all` if their old tab key isn't in the new set. The synonyms map
// below also rewrites known legacy keys so the most useful ones
// (action_needed → active, pending → active, in_progress → active,
// gifts → all) land on a sensible default rather than the bare All.
const VALID_TABS = new Set(["all", "active", "delivered", "cancelled"]);
const LEGACY_TAB_MAP: Record<string, string> = {
  action_needed: "active",
  pending: "active",
  in_progress: "active",
  gifts: "all",
};

export default function Orders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutFlow = useProductCheckoutFlow({ cameFrom: "orders" });
  const { confirmingSku, handleConfirmFromModal } = checkoutFlow;
  const [celebrationCheckout, setCelebrationCheckout] = useState<{
    celebrationId: string;
    recipientId: string;
    recipientName: string;
    occasion: string;
    defaultDate: string;
  } | null>(null);
  const {
    openWithCelebrationContext,
    setPanelOpen,
    setPanelExpanded,
    setCelebrationContext,
    setAdjustRecommendationSummary,
  } = useFloatingConciergeUi();

  const openFloatingConcierge = useCallback(() => {
    setCelebrationContext(null);
    setAdjustRecommendationSummary(null);
    setPanelOpen(true);
    setPanelExpanded(true);
  }, [
    setAdjustRecommendationSummary,
    setCelebrationContext,
    setPanelExpanded,
    setPanelOpen,
  ]);

  const initialTab = (() => {
    const fromUrl = searchParams.get("tab");
    if (!fromUrl) return "all";
    // MS390 Bug #16: rewrite legacy deep-link tab keys to the new
    // collapsed set, then fall back to "all" if still unknown.
    const remapped = LEGACY_TAB_MAP[fromUrl] ?? fromUrl;
    return VALID_TABS.has(remapped) ? remapped : "all";
  })();
  const [tab, setTab] = useState(initialTab);
  const ordersScrollYRef = useRef(0);

  // Keep ?tab in the URL in sync with the local state so deep links keep
  // working after the user clicks around. We skip pushing for the
  // default "all" so the URL stays clean for first-time visits.
  useEffect(() => {
    const current = searchParams.get("tab");
    if (tab === "all") {
      if (current) {
        const next = new URLSearchParams(searchParams);
        next.delete("tab");
        setSearchParams(next, { replace: true });
      }
      return;
    }
    if (current !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
  }, [tab, searchParams, setSearchParams]);

  const profileQuery = useProfile();
  const companyId = profileQuery.data?.company_id ?? null;
  const ordersQuery = useOrdersList(companyId);
  const needsOrderQuery = useApprovedCelebrationsPendingOrder(companyId);

  // MS526: page Sentry on ordersQuery failure. React Query holds the
  // error reference stable across retries until the next refetch, so
  // this fires roughly once per failure session. Tag `orders_loader`
  // matches the convention in OrderConfirmSheet (`order_confirm_*`)
  // so the inbox view stays sortable by surface.
  useEffect(() => {
    if (ordersQuery.isError && ordersQuery.error) {
      Sentry.captureException(ordersQuery.error, {
        tags: { scope: "orders_loader" },
      });
    }
  }, [ordersQuery.isError, ordersQuery.error]);

  const loading =
    profileQuery.isLoading ||
    (Boolean(companyId) && (ordersQuery.isLoading || needsOrderQuery.isLoading));
  // MS138: same useMemo perf pattern as MS136-137. The `?? []` fallback
  // created a fresh array reference on every render whenever the query
  // data was nullish; the 3 dependent useMemo hooks below
  // (actionNeededOrders + 2× filtered branches) re-ran every render
  // as a result. Orders is one of the most-visited pages so the win
  // here is real even at small data volumes.
  const orders = useMemo(() => ordersQuery.data ?? [], [ordersQuery.data]);
  const needsOrder = useMemo(() => needsOrderQuery.data ?? [], [needsOrderQuery.data]);
  const actionableNeedsOrder = useMemo(
    () => needsOrder.filter((cel) => !isPastCelebrationDate(cel.date)),
    [needsOrder],
  );

  const restoreOrdersScroll = useCallback(() => {
    requestAnimationFrame(() =>
      window.scrollTo({ top: ordersScrollYRef.current, behavior: "instant" }),
    );
  }, []);

  const dismissFulfillmentUi = useCallback(() => {
    setCelebrationCheckout(null);
    checkoutFlow.resetCheckout();
    restoreOrdersScroll();
  }, [checkoutFlow, restoreOrdersScroll]);

  const ordersFulfillmentReturnCtx = useMemo(
    () => ordersFulfillmentReturn(dismissFulfillmentUi),
    [dismissFulfillmentUi],
  );

  const runCelebrationCheckout = useCallback(
    async (celebrationId: string) => {
      if (!companyId) return;
      ordersScrollYRef.current = window.scrollY;
      const result = await openApprovedCelebrationCheckout(celebrationId, companyId);
      applyCelebrationCheckoutResult(result, {
        onCheckout: (sku, ctx) => {
          setCelebrationCheckout(ctx);
          handleConfirmFromModal(sku);
        },
        onPickGift: (ctx: CelebrationPickGiftSeed) => {
          openWithCelebrationContext({
            recipientId: ctx.recipientId,
            recipientName: ctx.recipientName,
            occasion: ctx.occasion,
            celebrationId: ctx.celebrationId,
            celebrationDate: ctx.defaultDate,
          });
        },
        onError: (message) => toast.error(message),
      });
    },
    [companyId, handleConfirmFromModal, openWithCelebrationContext],
  );

  useEffect(() => {
    const fulfillId = searchParams.get("fulfill");
    if (!fulfillId || confirmingSku || !companyId) return;
    const next = new URLSearchParams(searchParams);
    next.delete("fulfill");
    setSearchParams(next, { replace: true });
    void runCelebrationCheckout(fulfillId);
  }, [
    companyId,
    confirmingSku,
    runCelebrationCheckout,
    searchParams,
    setSearchParams,
  ]);

  const actionNeededOrders = useMemo(
    () => orders.filter((o) => isOrderActionNeeded(o)),
    [orders],
  );

  const filtered = useMemo(() => {
    if (tab === "all") return orders;
    if (tab === "active") {
      // MS390 Bug #16: union of pending + in_progress (the user
      // can still adjust these). Replaces the separate Pending +
      // In Progress + Action needed tabs.
      return orders.filter(
        (o) => o.status === "pending" || IN_PROGRESS_STATUSES.has(o.status),
      );
    }
    if (tab === "cancelled") {
      return orders.filter((o) => o.status === "cancelled");
    }
    if (tab === "delivered") {
      return orders.filter((o) => o.status === "delivered");
    }
    return orders;
  }, [orders, tab]);

  // MS390 Bug #16: action-needed (waiting on the buyer) is no longer
  // a dedicated tab, but the orphan-celebrations rail + the action-
  // needed orders still need a "things waiting on me" surface. We
  // pin them as a single coral-bordered prelude at the top of the
  // All and Active tabs. Hidden on Delivered + Cancelled (those are
  // terminal — nothing for the buyer to act on).
  const actionNeededCount = actionNeededOrders.length + actionableNeedsOrder.length;
  const showActionNeededRail =
    (tab === "all" || tab === "active") && actionNeededCount > 0;

  const floatingCheckoutActive =
    Boolean(celebrationCheckout) ||
    Boolean(confirmingSku) ||
    Boolean(checkoutFlow.viewingSku);
  useRegisterFloatingConciergeSuppression("orders-checkout", floatingCheckoutActive);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        aria-live="polite"
        aria-label="Loading orders"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    // MS253: PageShell breathing room for Orders. Wide variant
    // (max-w-6xl) so the order rows + tabs have room to read
    // without stretching to the viewport edges.
    <PageShell width="wide">
      {/* MS409: noindex — /orders is an authed-only order history page. */}
      <PageMeta
        title="Orders · Lembra"
        description="Your Lembra gift order history."
        path="/orders"
        noindex
      />
      {/* MS274: header + CTA was wrapped in a custom `flex items-center
          justify-between mb-8` which forced the row to stay side-by-
          side on mobile, squishing the "Orders" headline + subtitle.
          PageHeader.tail handles the stacking responsively — mobile
          stacks vertically, sm:+ floats the button right. The Sprint
          2.5a + /legacy-cleanup comments below still apply: outline
          variant, routes to /chat. */}
      <PageHeader
        title="Orders"
        emphasis="Orders"
        subtitle={`${orders.length} order${orders.length !== 1 ? "s" : ""} total. Everything you've sent, and what's on the way.`}
        tail={
          <Button variant="outline" onClick={openFloatingConcierge}>
            <Plus className="h-4 w-4" /> Create order
          </Button>
        }
      />

      {companyId && <BulkOrdersSection companyId={companyId} />}

      {ordersQuery.isError && (
        <div className="mb-6">
          <ErrorCard
            title="Couldn't load your orders"
            onRetry={() => ordersQuery.refetch()}
          />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white mb-6">
          {/* MS390 Bug #16: collapsed 7 tabs → 4. The action-needed
              + orphan-celebrations rail moved to a coral-bordered
              prelude at the top of All and Active (see below). */}
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active" className="gap-2">
            Active
            {actionNeededCount > 0 && (
              <Badge variant="coral" className="h-5 min-w-5 px-1.5 text-[10px]">
                {actionNeededCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="delivered">Delivered</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          {/* MS390 Bug #16: "things waiting on me" prelude. Renders
              above the filtered order list on the All + Active tabs
              when there are pending-unpaid orders or approved
              celebrations without any order yet. Replaces the old
              dedicated "Action needed" tab so the user doesn't have
              to click a tab to see what's blocking them — it surfaces
              at the top of the natural starting view. */}
          {showActionNeededRail && (
            <div className="mb-6 space-y-3">
              <p className="text-sm font-semibold text-coral">
                {actionNeededCount} item{actionNeededCount !== 1 ? "s" : ""} waiting on you.
              </p>
              {actionNeededOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onClick={() =>
                    navigate(
                      isGiftRequestOrder(order)
                        ? `/orders/${order.id}`
                        : `/orders/new?edit_order=${order.id}`,
                    )
                  }
                />
              ))}
              {/* Approved celebrations without any order yet. Opens
                  floating concierge checkout (or /calendar if member
                  join is missing). */}
              {actionableNeedsOrder.map((cel) => {
                const name = memberNameOf(cel.team_members);
                const member = memberLiteOf(cel.team_members);
                const primarySkuId =
                  cel.approved_recommendation?.line_items?.find((li) => li.sku_id)
                    ?.sku_id ?? null;
                const ctaLabel = primarySkuId ? "Complete checkout" : "Pick a gift";
                return (
                  <button
                    key={cel.id}
                    type="button"
                    onClick={() => {
                      if (!member) {
                        navigate("/calendar");
                        return;
                      }
                      void runCelebrationCheckout(cel.id);
                    }}
                    className="group w-full rounded-xl border border-charcoal/10 bg-white p-5 text-left shadow-sm transition-all hover:border-navy/20 hover:shadow-md"
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-xl"
                        style={{ background: BUTTER_TILE_BG }}
                        aria-hidden
                      >
                        {celebrationTypeEmoji(cel.type)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-0.5 flex flex-wrap items-center gap-2">
                          <span className="truncate font-bold text-navy">{name}</span>
                          <Badge variant="coral">Needs order</Badge>
                        </div>
                        <p className="text-sm text-charcoal/80">
                          {celebrationLabel(cel.type)} ·{" "}
                          {safeFormat(cel.date, "d MMM yyyy")}
                          {primarySkuId ? " · Gift locked in" : ""}
                        </p>
                      </div>
                      <span className="mt-1 hidden shrink-0 items-center gap-1.5 rounded-full bg-coral px-4 py-2 text-sm font-semibold text-white sm:inline-flex">
                        {ctaLabel}
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-3 border-t border-charcoal/10 pt-4 sm:hidden">
                      <span className="text-sm font-semibold text-coral">{ctaLabel}</span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-coral" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {filtered.length === 0 && !showActionNeededRail ? (
            <div className="text-center py-20">
              <Package className="mx-auto h-12 w-12 text-charcoal/30 mb-4" />
              <h2 className="text-xl font-bold text-navy mb-2">
                {orders.length === 0
                  ? "No orders yet"
                  : `No ${tab === "all" ? "" : tab} orders`}
              </h2>
              <p className="text-charcoal/60 mb-6">
                {orders.length === 0
                  ? "Ask the concierge for a gift idea, or approve a celebration on the calendar."
                  : "No orders match this filter."}
              </p>
              {orders.length === 0 && (
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button variant="primary" onClick={openFloatingConcierge}>
                    Ask the concierge <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/calendar")}>
                    Go to Calendar
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* MS390 Bug #16: skip orders that already appear in
                  the prelude above (action-needed rows) so they
                  don't render twice on the same view. */}
              {filtered
                .filter(
                  (o) => !(showActionNeededRail && isOrderActionNeeded(o)),
                )
                .map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onClick={() => {
                      if (isOrderActionNeeded(order)) {
                        navigate(
                          isGiftRequestOrder(order)
                            ? `/orders/${order.id}`
                            : `/orders/new?edit_order=${order.id}`,
                        );
                      } else {
                        navigate(`/orders/${order.id}`);
                      }
                    }}
                  />
                ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ProductCheckoutModals
        flow={checkoutFlow}
        recipientName={celebrationCheckout?.recipientName ?? ""}
        recipientId={celebrationCheckout?.recipientId ?? null}
        occasion={celebrationCheckout?.occasion ?? "other"}
        defaultDate={celebrationCheckout?.defaultDate}
        celebrationId={celebrationCheckout?.celebrationId ?? null}
        isAnonymous={false}
        fulfillmentReturn={
          celebrationCheckout || checkoutFlow.viewingSku || checkoutFlow.confirmingSku
            ? ordersFulfillmentReturnCtx
            : null
        }
        onCheckoutClose={dismissFulfillmentUi}
      />
    </PageShell>
  );
}
